import React, { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Custom Hooks
import {
  useCreateFuelType,
  useDeleteFuelType,
  useEditFuelType,
  useFuelTypes,
} from "../../../hooks/fuelTypeHook"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import { useToastMessage } from "hooks/useToastMessage"

const FuelTypeList = () => {
  document.title = "Fuel Type List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [fuelType, setFuelType] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { handleResponse, handleError } = useToastMessage()

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: fuelTypesResponse,
    isLoading,
    refetch: fuelTypeRefetch,
    isRefetching: fuelTypeRefetchLoading,
  } = useFuelTypes(queryParams)

  const { mutateAsync: createFuelType, isPending: fuelTypeCreateLoading } =
    useCreateFuelType()
  const { mutateAsync: editFuelType, isPending: fuelTypeEditLoading } =
    useEditFuelType()
  const { mutateAsync: deleteFuelType, isPending: fuelTypeDeleteLoading } =
    useDeleteFuelType()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setFuelType(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setFuelType(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleFuelTypeClick = useCallback(
    arg => {
      setFuelType({ id: arg.id, name: arg.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(fuelTypeData => {
    setFuelType(fuelTypeData)
    setDeleteModal(true)
  }, [])

  const handleDeleteFuelType = useCallback(async () => {
    if (fuelType?.id) {
      try {
        const res = await deleteFuelType(fuelType.id)
        toast.success(res?.message ?? "Fuel type deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete fuel type:", error)
        toast.error("Failed to delete fuel type.")
      }
    }
  }, [fuelType, deleteFuelType])

  const handleFuelTypeListRefresh = () => {
    fuelTypeRefetch()
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .required("Please enter fuel type name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = { name: values.name }
      const action = isEdit
        ? editFuelType({ id: fuelType.id, ...payload })
        : createFuelType(payload)

      try {
        const res = await action

        handleResponse(
          res,
          `Fuel type ${isEdit ? "updated" : "created"} successfully.`
        )
        if (res.success) resetForm()
        toggle()
      } catch (error) {
        console.error(error)
        handleError(error, "Failed to save fuel type.")
      }
    },
    [isEdit, fuelType, editFuelType, createFuelType, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: fuelType?.name || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleFuelTypeClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleFuelTypeClick, onClickDelete]
  )

  const fuelTypesData = fuelTypesResponse?.data?.data || []
  const paginationInfo = fuelTypesResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteFuelType}
        loading={fuelTypeDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Fuel Type" breadcrumbItem="Fuel Type Lists" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">
                      Fuel Type Lists
                    </h5>
                    <div className="flex-shrink-0">
                      <Button
                        color="primary"
                        onClick={handleAddNewClick}
                        className="me-1"
                      >
                        Add New Fuel Type
                      </Button>
                      <Button
                        color="light"
                        onClick={handleFuelTypeListRefresh}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            fuelTypeRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                          id="resetTooltip"
                        >
                          <i className="mdi mdi-close" />
                          <UncontrolledTooltip
                            placement="top"
                            target="resetTooltip"
                          >
                            Reset Filters
                          </UncontrolledTooltip>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={fuelTypesData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || fuelTypeRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle} tag="h4">
              {isEdit ? "Edit Fuel Type" : "Add Fuel Type"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Fuel Type Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter fuel type name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={fuelTypeEditLoading || fuelTypeCreateLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Fuel Type"}
                    {(fuelTypeEditLoading || fuelTypeCreateLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default FuelTypeList
